import { useEffect } from 'react'
import { FaArrowRight, FaCheckCircle, FaExternalLinkAlt } from 'react-icons/fa'

import { trackEvent } from 'services/analytics'

import * as C from './styles'

type TravelService = {
  title: string
  description: string
  image: string
  url: string
  highlights: string[]
  cta: string
}

const services: TravelService[] = [
  {
    title: 'Passagens aéreas',
    description:
      'Pesquise opções de voos para começar a transformar o roteiro em uma viagem de verdade.',
    image: '/images/dicas/descontos.jpeg',
    url: 'https://www.passagenspromo.com.br/?utm_medium=afiliado&pcrid=4662&utm_source=site-blog',
    highlights: [
      'Pesquisa de passagens aéreas',
      'Compare alternativas para o seu destino'
    ],
    cta: 'Pesquisar passagens'
  },
  {
    title: 'Hospedagens',
    description:
      'Pesquise hotéis, apartamentos, pousadas e outras opções de hospedagem para o seu destino.',
    image: '/images/dicas/hospedagem.jpeg',
    url: 'https://www.booking.com/index.pt-br.html?aid=376377&label=booking-name-pt-row-bwMffLz%2AfdB8PTKNsC9tlgS267778091920%3Apl%3Ata%3Ap1%3Ap22.563.000%3Aac%3Aap%3Aneg%3Afi%3Atikwd-65526620%3Alp1001775%3Ali%3Adec%3Adm%3Appccp%3DUmFuZG9tSVYkc2RlIyh9YXwxhKG0pUU-3JdcXtALQMg&sid=bf844347c8a2442294f8f9502f2ec724',
    highlights: [
      'Grande variedade de acomodações',
      'Compare opções no mesmo lugar'
    ],
    cta: 'Buscar hospedagem'
  },
  {
    title: 'Aluguel de carros',
    description:
      'Compare locadoras e encontre uma opção de carro adequada ao seu roteiro no Brasil ou no exterior.',
    image: '/images/dicas/aluguel.jpeg',
    url: 'https://naiawork.com/g/wqjhitsyjq16e04831795ea594bb46/?ulp=https%3A%2F%2Fwww.discovercars.com%2Fbr',
    highlights: [
      'Comparação entre locadoras',
      'Reserva antecipada em português'
    ],
    cta: 'Comparar carros'
  },
  {
    title: 'Experiências e passeios',
    description:
      'Encontre atrações, tours, ingressos e atividades para complementar os dias do seu roteiro.',
    image: '/images/dicas/planejamento.jpg',
    url: 'https://www.viator.com/pt-BR/?pid=P00037907&mcid=42383&medium=link&campaign=Geral',
    highlights: [
      'Passeios em destinos do mundo todo',
      'Atividades para diferentes perfis de viagem'
    ],
    cta: 'Explorar experiências'
  },
  {
    title: 'eSIM internacional',
    description:
      'Chegue ao destino com internet disponível para mapas, mensagens, reservas e aplicativos durante a viagem.',
    image: '/images/dicas/chip.jpg',
    url: 'https://www.awin1.com/cread.php?awinmid=121892&awinaffid=2850925&clickref=global&clickref2=planeje&clickref3=esim&ued=https%3A%2F%2Fusims.com%2Fget-my-esim%2F',
    highlights: [
      'Internet para usar durante a viagem',
      'Planos para diferentes destinos'
    ],
    cta: 'Ver planos de internet'
  },
  {
    title: 'Seguro viagem',
    description:
      'Compare planos e coberturas para viajar com proteção em emergências médicas e outros imprevistos.',
    image: '/images/dicas/seguro.jpeg',
    url: 'https://www.awin1.com/cread.php?awinmid=120992&awinaffid=3120916&clickref=global&clickref2=planeje&clickref3=seguro&ued=https%3A%2F%2Fmelhorseguro.com.br%2F',
    highlights: [
      'Compare diferentes seguradoras',
      'Coberturas para viagens nacionais e internacionais'
    ],
    cta: 'Comparar seguros'
  }
]

const Suggestions = () => {
  useEffect(() => {
    services.forEach((service) =>
      trackEvent({
        eventType: 'commercial_impression',
        entityType: 'partner',
        entityId: service.title,
        value: service.title,
        metadata: { source: 'planeje' }
      })
    )
  }, [])

  return (
    <C.Container>
      <C.Hero>
        <C.HeroInner>
          <C.Eyebrow>Planeje sua viagem</C.Eyebrow>
          <C.HeroTitle>
            Tudo o que você precisa em <strong>menos etapas</strong>
          </C.HeroTitle>
          <C.HeroSubtitle>
            Reunimos em um só lugar serviços úteis para transformar seu roteiro
            em viagem: hospedagem, passeios, transporte, conectividade e
            proteção.
          </C.HeroSubtitle>
        </C.HeroInner>
      </C.Hero>

      <C.Content>
        <C.SectionIntro>
          <div>
            <C.SectionTitle>Escolha o que você precisa</C.SectionTitle>
            <C.SectionDescription>
              As principais informações ficam aqui. O botão leva ao site externo
              para consultar condições e concluir sua escolha.
            </C.SectionDescription>
          </div>
          <C.FlowHint>
            <span>Roteiro</span>
            <FaArrowRight />
            <span>Planejamento</span>
            <FaArrowRight />
            <strong>Viagem</strong>
          </C.FlowHint>
        </C.SectionIntro>

        <C.Grid>
          {services.map((service) => (
            <C.Card key={service.title}>
              <C.CardImage $image={service.image} />

              <C.CardBody>
                <C.CardTitle>{service.title}</C.CardTitle>
                <C.CardDescription>{service.description}</C.CardDescription>

                <C.Highlights>
                  {service.highlights.map((highlight) => (
                    <li key={highlight}>
                      <FaCheckCircle />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </C.Highlights>

                <C.PartnerButton
                  href={service.url}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  onClick={() =>
                    trackEvent({
                      eventType: 'commercial_click',
                      entityType: 'partner',
                      entityId: service.title,
                      value: service.title,
                      metadata: {
                        source: 'planeje'
                      }
                    })
                  }
                >
                  <span>{service.cta}</span>
                  <FaExternalLinkAlt />
                </C.PartnerButton>
              </C.CardBody>
            </C.Card>
          ))}
        </C.Grid>
      </C.Content>
    </C.Container>
  )
}

export default Suggestions
